console.log("VPX ENGINE FILE LOADED");

(function () {

    if (!window.VORYNEX) {
        console.error("VORYNEX namespace not found. Engine aborted.");
        return;
    }

    const Engine = {};

    /*
    ==========================================
    Static Reference Data
    ------------------------------------------
    Supplement catalogue uses i18n KEYS (not
    literal text) for frequency labels, so
    vpx-render.js can translate them via
    window.VORYNEX.i18n.t(). Meal plans are now
    generated by vpx-meal-generator.js (real
    foods/calories) — FALLBACK_MEAL_PLAN below
    is only used if that module fails to load.
    ==========================================
    */

    const FALLBACK_MEAL_PLAN = [
        { icon: "🥣", nameKey: "meal_breakfast", items: [], kcal: 0 },
        { icon: "🍗", nameKey: "meal_lunch", items: [], kcal: 0 },
        { icon: "🍌", nameKey: "meal_preWorkout", items: [], kcal: 0 },
        { icon: "🥤", nameKey: "meal_postWorkout", items: [], kcal: 0 },
        { icon: "🐟", nameKey: "meal_dinner", items: [], kcal: 0 }
    ];

    /*
    ==========================================
    Supplement Recommendations
    ------------------------------------------
    Maps to 5 REAL collections on the live store
    (not individual products — the merchant
    organizes inventory by category). Products
    themselves are pulled live via Liquid into
    window.VPX_COLLECTIONS; this logic decides
    WHICH collections are relevant to this
    profile, WHY, and the minimum consistent-use
    duration before a meaningful result is
    reasonable to expect — grounded in common
    sports-nutrition practice, not guesswork:
      - Creatine: saturation typically takes
        ~3-4 weeks of daily use.
      - Collagen/joint support: research on
        joint-comfort benefits typically looks
        at ~8-12 week windows.
      - Protein & pre-workout: no "loading"
        period — effect is about consistent
        daily/per-session use, so minWeeks is
        null for these.
    IMPORTANT: every recommendation set includes
    a disclaimer (rendered in vpx-render.js) that
    supplements support — never replace — correct
    nutrition, training, and sleep.
    ==========================================
    */

    function buildSupplementRecommendations(profile) {

        const recs = [];

        // Protein — foundational for every goal.
        recs.push({
            collectionHandle: "proteins",
            reasonKey: "supp_reason_protein",
            minWeeks: null
        });

        // Creatine — strongest case for muscle gain, but also
        // valuable for anyone training with real frequency.
        if (profile.goal === "muscle_gain" || profile.trainingDaysPerWeek >= 3) {
            recs.push({
                collectionHandle: "creatine-monohydrate",
                reasonKey: profile.goal === "muscle_gain" ? "supp_reason_creatine_gain" : "supp_reason_creatine_general",
                minWeeks: 4
            });
        }

        // Pre-workout — only relevant at meaningful training frequency.
        if (profile.trainingDaysPerWeek >= 3) {
            recs.push({
                collectionHandle: "pre-workout",
                reasonKey: "supp_reason_preworkout",
                minWeeks: null
            });
        }

        // Vitamins & Electrolytes — calorie-restricted diets and/or
        // higher training frequency both increase micronutrient needs.
        if (profile.goal === "fat_loss" || profile.trainingDaysPerWeek >= 4) {
            recs.push({
                collectionHandle: "vitamins-electrolytes",
                reasonKey: profile.goal === "fat_loss" ? "supp_reason_vitamins_fatloss" : "supp_reason_vitamins_training",
                minWeeks: 3
            });
        }

        // Collagen / Joint Support — relevant with age-related joint
        // wear or high training volume placing load on joints.
        if (profile.age >= 35 || profile.trainingDaysPerWeek >= 5) {
            recs.push({
                collectionHandle: "collagen-joint-support",
                reasonKey: profile.age >= 35 ? "supp_reason_collagen_age" : "supp_reason_collagen_training",
                minWeeks: 8
            });
        }

        return recs;
    }

    /*
    ==========================================
    Layer 1 — BMR (multi-equation)
    ==========================================
    */

    function bmrMifflin(profile) {
        const base = 10 * profile.weightKg + 6.25 * profile.heightCm - 5 * profile.age;
        return profile.gender === "male" ? base + 5 : base - 161;
    }

    function leanBodyMass(weightKg, bodyFatPct) {
        if (bodyFatPct === null || bodyFatPct === undefined) return null;
        return weightKg * (1 - bodyFatPct / 100);
    }

    function bmrKatchMcArdle(lbm) {
        return 370 + 21.6 * lbm;
    }

    function bmrCunningham(lbm) {
        return 500 + 22 * lbm;
    }

    function bmrHarrisBenedict(profile) {
        // Reference-only, never used as primary source.
        if (profile.gender === "male") {
            return 88.362 + (13.397 * profile.weightKg) + (4.799 * profile.heightCm) - (5.677 * profile.age);
        }
        return 447.593 + (9.247 * profile.weightKg) + (3.098 * profile.heightCm) - (4.330 * profile.age);
    }

    function calculateBMR(profile, resolvedBodyFatPct) {

        const lbm = leanBodyMass(profile.weightKg, resolvedBodyFatPct);
        const mifflin = bmrMifflin(profile);
        const harrisBenedict = bmrHarrisBenedict(profile);

        let primary = mifflin;
        let equationUsed = "Mifflin-St Jeor";

        const isHighlyTrained = profile.trainingDaysPerWeek >= 5;
        const leanThreshold = profile.gender === "male" ? 15 : 22;

        if (lbm !== null) {
            if (isHighlyTrained && resolvedBodyFatPct < leanThreshold) {
                primary = bmrCunningham(lbm);
                equationUsed = "Cunningham";
            } else {
                primary = bmrKatchMcArdle(lbm);
                equationUsed = "Katch-McArdle";
            }
        }

        return {
            value: primary,
            equationUsed: equationUsed,
            lbm: lbm,
            comparison: {
                mifflin: Math.round(mifflin),
                harrisBenedict: Math.round(harrisBenedict),
                katchMcArdle: lbm !== null ? Math.round(bmrKatchMcArdle(lbm)) : null,
                cunningham: lbm !== null ? Math.round(bmrCunningham(lbm)) : null
            }
        };
    }

    /*
    ==========================================
    U.S. Navy Circumference Method
    ------------------------------------------
    Public-domain body fat estimation formula
    (Hodgdon & Beckett, 1984), metric (cm) form.
    Accuracy is approximately ±3-4% versus DEXA —
    not exact, but meaningfully better than a
    guess when no other measurement exists.
    ==========================================
    */

    function calculateNavyBodyFat(profile) {

        const neck = profile.neckCm;
        const waist = profile.waistCm;
        const hip = profile.hipCm;
        const heightCm = profile.heightCm;

        if (!neck || !waist) return null;
        if (profile.gender === "female" && !hip) return null;

        let bf;

        if (profile.gender === "male") {
            const diff = waist - neck;
            if (diff <= 0) return null;
            bf = 495 / (1.0324 - 0.19077 * Math.log10(diff) + 0.15456 * Math.log10(heightCm)) - 450;
        } else {
            const diff = waist + hip - neck;
            if (diff <= 0) return null;
            bf = 495 / (1.29579 - 0.35004 * Math.log10(diff) + 0.22100 * Math.log10(heightCm)) - 450;
        }

        if (!isFinite(bf) || bf <= 0 || bf >= 70) return null;

        return Math.round(bf * 10) / 10;
    }

    /*
    ==========================================
    Resolve Body Fat %
    ------------------------------------------
    Combines the optional self-reported slider
    estimate with the optional Navy measurement
    estimate. The Navy estimate is more objective
    (based on actual circumferences), so when
    both exist it is weighted more heavily.
    ==========================================
    */

    function resolveBodyFatPct(profile) {

        const sliderEstimate = (profile.bodyFatPct !== null && profile.bodyFatPct !== undefined)
            ? profile.bodyFatPct
            : null;

        const navyEstimate = calculateNavyBodyFat(profile);

        if (sliderEstimate !== null && navyEstimate !== null) {
            // Navy measurement weighted 65%, self-reported slider 35%.
            const blended = (navyEstimate * 0.65) + (sliderEstimate * 0.35);
            return { value: Math.round(blended * 10) / 10, source: "blended" };
        }

        if (navyEstimate !== null) {
            return { value: navyEstimate, source: "navy" };
        }

        if (sliderEstimate !== null) {
            return { value: sliderEstimate, source: "slider" };
        }

        return { value: null, source: "none" };
    }

    /*
    ==========================================
    Layer 2 — NEAT (estimate, not measurement)
    ==========================================
    */

    const NEAT_FACTOR_BY_JOB = {
        sedentary: 0.20,
        standing: 0.30,
        active: 0.40
    };

    function calculateNEAT(profile, bmr) {
        const factor = NEAT_FACTOR_BY_JOB[profile.jobType] || NEAT_FACTOR_BY_JOB.sedentary;
        return bmr * factor;
    }

    /*
    ==========================================
    Layer 3 — Exercise Calories (MET-based)
    ==========================================
    */

    const RESISTANCE_TRAINING_MET = 6;
    const ASSUMED_SESSION_HOURS = 1;

    function calculateExerciseCalories(profile) {
        // NET calories: (MET - 1) × kg × hours. The gross MET formula
        // (MET × kg × h) INCLUDES the resting metabolism of that hour,
        // which is already counted inside BMR — using gross here would
        // double-count the training hour (~45+ kcal/day systematic
        // overestimate for a typical 80kg / 4-day lifter).
        const perSession = (RESISTANCE_TRAINING_MET - 1) * profile.weightKg * ASSUMED_SESSION_HOURS;
        const weeklyTotal = perSession * profile.trainingDaysPerWeek;
        return weeklyTotal / 7; // averaged into daily TDEE
    }

    /*
    ==========================================
    Layer 4 — TEF (Thermic Effect of Food)
    ==========================================
    */

    const TEF_BLENDED_FACTOR = 1.08;

    /*
    ==========================================
    Layers 6/7/8 — Bounded Heuristic Modifiers
    ------------------------------------------
    Small, capped adjustments based on general
    behavioral trends — not precise physiological
    models. Always disclosed as estimates.
    ==========================================
    */

    function sleepModifier(sleepHours) {
        if (sleepHours < 6) return -0.03;
        if (sleepHours < 7) return -0.01;
        if (sleepHours > 9) return -0.01;
        return 0;
    }

    function stressModifier(stressLevel) {
        if (stressLevel >= 4) return -0.02;
        if (stressLevel === 3) return -0.01;
        return 0;
    }

    function experienceModifier(trainingDaysPerWeek) {
        if (trainingDaysPerWeek >= 5) return 0.02;
        return 0;
    }

    /*
    ==========================================
    Goal → Calorie Target
    ==========================================
    */

    const PACE_DEFICIT_KCAL = {
        slow: 300,
        moderate: 500,
        aggressive: 750
    };

    const PACE_SURPLUS_KCAL = {
        slow: 150,
        moderate: 300,
        aggressive: 500
    };

    function calculateTargetCalories(tdee, goal, bmr, paceLevel) {

        let target = tdee;

        if (goal === "fat_loss") {
            const deficit = PACE_DEFICIT_KCAL[paceLevel] || PACE_DEFICIT_KCAL.moderate;
            target = tdee - deficit;
        } else if (goal === "muscle_gain") {
            const surplus = PACE_SURPLUS_KCAL[paceLevel] || PACE_SURPLUS_KCAL.moderate;
            target = tdee + surplus;
        }

        // Safety floor: never recommend below BMR. When the floor
        // kicks in, the flag lets the UI TELL the person their chosen
        // pace was capped for safety — previously this happened
        // silently, so someone could pick "aggressive" and unknowingly
        // receive a smaller deficit than the label implied.
        const flooredToBmr = target < bmr;

        return {
            value: Math.round(Math.max(target, bmr)),
            flooredToBmr: flooredToBmr
        };
    }

    /*
    ==========================================
    Macro Engine
    ==========================================
    */

    /*
    Protein targets use DIFFERENT per-kg constants depending on the
    reference mass, because the literature quotes them differently:
      - Per kg of TOTAL body weight: ~1.6-2.2 g/kg (used when body
        fat % is unknown).
      - Per kg of LEAN body mass: ~2.3-3.1 g/kg while cutting
        (Helms et al. 2014), proportionally scaled for other goals
        (used when body fat % IS known, since LBM excludes fat mass).
    Previously a single table was applied to whichever mass was
    available, which meant a person who PROVIDED body fat % got a
    LOWER (and less correct) protein target than one who skipped it.
    */

    const PROTEIN_G_PER_KG_TOTAL_BY_GOAL = {
        fat_loss: 2.2,
        maintenance: 1.8,
        muscle_gain: 2.0
    };

    const PROTEIN_G_PER_KG_LBM_BY_GOAL = {
        fat_loss: 2.6,
        maintenance: 2.2,
        muscle_gain: 2.4
    };

    const FAT_PCT_OF_CALORIES = 0.25;

    function calculateMacros(targetCalories, profile, goal, resolvedBodyFatPct) {

        const usingLbm = resolvedBodyFatPct !== null;
        const proteinTable = usingLbm ? PROTEIN_G_PER_KG_LBM_BY_GOAL : PROTEIN_G_PER_KG_TOTAL_BY_GOAL;
        const proteinPerKg = proteinTable[goal] || proteinTable.maintenance;
        const referenceWeight = usingLbm
            ? leanBodyMass(profile.weightKg, resolvedBodyFatPct)
            : profile.weightKg;

        const proteinG = Math.round(proteinPerKg * referenceWeight);
        const proteinKcal = proteinG * 4;

        const fatKcal = targetCalories * FAT_PCT_OF_CALORIES;
        const fatG = Math.round(fatKcal / 9);

        const remainingKcal = Math.max(0, targetCalories - proteinKcal - fatKcal);
        const carbsG = Math.round(remainingKcal / 4);

        return {
            protein: proteinG,
            carbs: carbsG,
            fat: fatG,
            proteinKcal: Math.round(proteinKcal),
            carbsKcal: Math.round(carbsG * 4),
            fatKcal: Math.round(fatKcal)
        };
    }

    /*
    ==========================================
    Water Engine (rule-based, not a strict model)
    ==========================================
    */

    function calculateWaterTarget(profile) {
        let liters = profile.weightKg * 0.035;
        if (profile.trainingDaysPerWeek > 0) liters += 0.5;
        return Math.round(liters * 10) / 10;
    }

    /*
    ==========================================
    Confidence Score™
    ------------------------------------------
    DATA COMPLETENESS score, not a statistical
    confidence interval. Weighted by how much
    each optional field improves the estimate.
    ==========================================
    */

    function calculateConfidence(profile, bodyFatSource) {

        const checklist = [
            { labelKey: "onb_review_age", present: true, weight: 30 },
            { labelKey: "onb_review_jobType", present: !!profile.jobType, weight: 10 },
            { labelKey: "onb_review_trainingPerWeek", present: profile.trainingDaysPerWeek !== null, weight: 10 },
            { labelKey: "onb_review_sleep", present: !!profile.sleepHours, weight: 10 },
            { labelKey: "onb_review_bodyFat", present: bodyFatSource === "slider" || bodyFatSource === "blended", weight: 10 },
            { labelKey: "onb_review_measurements", present: bodyFatSource === "navy" || bodyFatSource === "blended", weight: 20 },
            { labelKey: "onb_review_stress", present: !!profile.stressLevel, weight: 10 }
        ];

        const score = checklist.reduce(function (sum, item) {
            return sum + (item.present ? item.weight : 0);
        }, 0);

        return { score: score, checklist: checklist };
    }

    /*
    ==========================================
    Heuristic Scores (Dashboard radar)
    ==========================================
    */

    function calculateHeuristicScores(profile, confidence) {

        const training = Math.round(Math.min(100, (profile.trainingDaysPerWeek / 6) * 100));
        const recovery = Math.round(Math.min(100, (profile.sleepHours / 8) * 100));
        const mindset = Math.round(Math.max(0, ((6 - profile.stressLevel) / 5) * 100));
        const lifestyle = Math.round(Math.max(0, Math.min(100,
            70 + (profile.jobType === "active" ? 10 : 0) - (profile.stressLevel - 3) * 8
        )));
        const hydration = 75; // static default until water-intake logging exists
        const nutrition = Math.round(Math.min(100, 60 + confidence.score * 0.3));

        const overall = Math.round(
            (training + recovery + mindset + lifestyle + hydration + nutrition) / 6
        );

        return { training, recovery, mindset, lifestyle, hydration, nutrition, overall };
    }

    /*
    ==========================================
    Goal Projection
    ==========================================
    */

    const GOAL_PHASE_KEY = {
        fat_loss: "phase_cutting",
        muscle_gain: "phase_bulking",
        maintenance: "phase_maintenance"
    };

    const KCAL_PER_KG_BODY_MASS = 7700;

    /*
    ==========================================
    Adaptive Projection (week-by-week simulation)
    ------------------------------------------
    Linear projections (constant rate × weeks) systematically
    overestimate loss: the NIH Body Weight Planner work (Hall et al.)
    showed a fixed 500 kcal deficit delivers roughly half the
    "3500 kcal per pound" prediction over long horizons, because:
      1) BMR falls as body mass falls (less tissue to maintain), and
      2) adaptive thermogenesis makes expenditure drop a further
         ~0.5%/week of dieting, plateauing around 10% (measured
         adaptation in trials is on the order of ~90-100 kcal/day
         after ~8-16 weeks).
    This simulation recomputes the effective deficit EVERY week:
    intake stays fixed at targetCalories (what the person actually
    eats), while TDEE declines with weight + adaptation — so the
    curve starts at the chosen pace and honestly flattens.
    For muscle gain the same iteration runs WITHOUT the adaptation
    term (adaptation to surplus is far smaller); BMR rising with
    weight naturally shrinks the surplus slightly over time.
    ==========================================
    */

    const WEEKLY_ADAPTATION_RATE = 0.005;   // 0.5% of TDEE per dieting week
    const MAX_ADAPTATION = 0.10;            // capped at 10% total

    function simulateAdaptiveProjection(profile, targetCalories, initialTdee, initialBmr, programWeeks) {

        const isLoss = profile.goal === "fat_loss";
        const weeklyCurve = [{ week: 0, weight: profile.weightKg }];

        let weight = profile.weightKg;

        for (let week = 1; week <= programWeeks; week++) {

            // TDEE scales with BMR as weight changes; Mifflin is linear
            // in weight (10 kcal per kg), so re-derive proportionally
            // instead of re-running the whole layered pipeline per week.
            const bmrNow = initialBmr + 10 * (weight - profile.weightKg);
            const tdeeScale = bmrNow / initialBmr;
            let tdeeNow = initialTdee * tdeeScale;

            if (isLoss) {
                const adaptation = Math.min(MAX_ADAPTATION, WEEKLY_ADAPTATION_RATE * (week - 1));
                tdeeNow = tdeeNow * (1 - adaptation);
            }

            const dailyDelta = targetCalories - tdeeNow; // negative in a deficit
            const weeklyChangeKg = (dailyDelta * 7) / KCAL_PER_KG_BODY_MASS;

            // In a deficit, never simulate past the point where the
            // (falling) TDEE meets intake — deficit has closed.
            if (isLoss && dailyDelta >= 0) {
                weeklyCurve.push({ week: week, weight: Math.round(weight * 10) / 10 });
                continue;
            }

            weight = weight + weeklyChangeKg;
            weeklyCurve.push({ week: week, weight: Math.round(weight * 10) / 10 });
        }

        return weeklyCurve;
    }

    function calculateGoalProjection(profile, targetCalories, tdee, bmr) {

        if (profile.goal === "maintenance") {
            return {
                phaseKey: GOAL_PHASE_KEY.maintenance,
                weightDeltaKg: 0,
                currentWeight: profile.weightKg,
                targetWeight: profile.weightKg,
                unit: "kg",
                weeksRemaining: 0,
                weeklyCurve: [],
                linearTargetWeight: profile.weightKg
            };
        }

        const programWeeks = profile.programWeeks || 12;

        // Naive linear estimate — kept for comparison display
        // ("what other calculators would tell you").
        const dailyDelta = targetCalories - tdee;
        const linearWeeklyRateKg = Math.abs(dailyDelta * 7) / KCAL_PER_KG_BODY_MASS;
        const linearChangeKg = Math.round(linearWeeklyRateKg * programWeeks * 10) / 10;
        const linearTargetWeight = profile.goal === "fat_loss"
            ? Math.round((profile.weightKg - linearChangeKg) * 10) / 10
            : Math.round((profile.weightKg + linearChangeKg) * 10) / 10;

        // Adaptive (honest) estimate — the primary number shown.
        const weeklyCurve = simulateAdaptiveProjection(profile, targetCalories, tdee, bmr, programWeeks);
        const targetWeight = weeklyCurve[weeklyCurve.length - 1].weight;
        const avgWeeklyRateKg = Math.abs(targetWeight - profile.weightKg) / programWeeks;

        return {
            phaseKey: GOAL_PHASE_KEY[profile.goal] || "phase_maintenance",
            weightDeltaKg: Math.round((targetWeight - profile.weightKg) * 10) / 10,
            currentWeight: profile.weightKg,
            targetWeight: targetWeight,
            unit: "kg",
            weeksRemaining: programWeeks,
            weeklyRateKg: Math.round(avgWeeklyRateKg * 100) / 100,
            weeklyCurve: weeklyCurve,
            linearTargetWeight: linearTargetWeight
        };
    }

    /*
    ==========================================
    Root Calculation
    ==========================================
    */

    Engine.calculate = function (profile) {

        const bodyFat = resolveBodyFatPct(profile);
        const resolvedBodyFatPct = bodyFat.value;

        const bmrResult = calculateBMR(profile, resolvedBodyFatPct);
        const bmr = bmrResult.value;

        const neat = calculateNEAT(profile, bmr);
        const exerciseCalories = calculateExerciseCalories(profile);

        const maintenanceEstimate = bmr + neat + exerciseCalories;
        let tdee = maintenanceEstimate * TEF_BLENDED_FACTOR;

        const modifierTotal =
            sleepModifier(profile.sleepHours) +
            stressModifier(profile.stressLevel) +
            experienceModifier(profile.trainingDaysPerWeek);

        tdee = tdee * (1 + modifierTotal);

        const targetResult = calculateTargetCalories(tdee, profile.goal, bmr, profile.paceLevel);
        const targetCalories = targetResult.value;
        const macros = calculateMacros(targetCalories, profile, profile.goal, resolvedBodyFatPct);

        /*
        Calorie Cycling — different targets for training vs rest days.
        The averaged `targetCalories` above spreads exercise energy
        evenly across the week; here the SAME weekly total is instead
        distributed to match actual daily demand: training days carry
        the full net session cost, rest days don't. The chosen deficit/
        surplus is applied to each day's own TDEE, so the weekly
        average still equals the flat target — this changes WHEN the
        person eats more, not HOW MUCH overall. Protein stays constant
        across day types (it's set per kg of mass, independent of the
        day's energy); fat stays at its % share of that day's calories
        and carbs absorb the difference — i.e. the extra training-day
        energy arrives mostly as carbohydrate around the sessions,
        which is exactly the standard sports-nutrition convention.
        */

        const modifierFactor = 1 + modifierTotal;
        const perSessionNetKcal = (profile.trainingDaysPerWeek > 0)
            ? (6 - 1) * profile.weightKg * 1  // matches calculateExerciseCalories constants
            : 0;

        const restDayTdee = (bmr + neat) * TEF_BLENDED_FACTOR * modifierFactor;
        const trainingDayTdee = (bmr + neat + perSessionNetKcal) * TEF_BLENDED_FACTOR * modifierFactor;

        const trainingDayTargetResult = calculateTargetCalories(trainingDayTdee, profile.goal, bmr, profile.paceLevel);
        const restDayTargetResult = calculateTargetCalories(restDayTdee, profile.goal, bmr, profile.paceLevel);

        const trainingDayMacros = calculateMacros(trainingDayTargetResult.value, profile, profile.goal, resolvedBodyFatPct);
        const restDayMacros = calculateMacros(restDayTargetResult.value, profile, profile.goal, resolvedBodyFatPct);
        const water = calculateWaterTarget(profile);
        const confidence = calculateConfidence(profile, bodyFat.source);
        const scores = calculateHeuristicScores(profile, confidence);
        const goal = calculateGoalProjection(profile, targetCalories, tdee, bmr);

        const macroKcalTotal = macros.proteinKcal + macros.carbsKcal + macros.fatKcal;

        const calorieBreakdown = [
            {
                labelKey: "macro_protein", kcal: macros.proteinKcal,
                pct: Math.round((macros.proteinKcal / macroKcalTotal) * 100),
                color: "var(--vpx-green)"
            },
            {
                labelKey: "macro_carbs", kcal: macros.carbsKcal,
                pct: Math.round((macros.carbsKcal / macroKcalTotal) * 100),
                color: "var(--vpx-gold)"
            },
            {
                labelKey: "macro_fat", kcal: macros.fatKcal,
                pct: Math.round((macros.fatKcal / macroKcalTotal) * 100),
                color: "var(--vpx-red)"
            }
        ];

        const macrosForUI = [
            { labelKey: "macro_protein", value: macros.protein, unit: "g", pct: calorieBreakdown[0].pct, color: "var(--vpx-green)" },
            { labelKey: "macro_carbs", value: macros.carbs, unit: "g", pct: calorieBreakdown[1].pct, color: "var(--vpx-gold)" },
            { labelKey: "macro_fat", value: macros.fat, unit: "g", pct: calorieBreakdown[2].pct, color: "var(--vpx-red)" }
        ];

        const supplementRecommendations = buildSupplementRecommendations(profile);

        const mealPlan = (window.VORYNEX.mealGenerator && window.VORYNEX.mealGenerator.generate)
            ? window.VORYNEX.mealGenerator.generate(targetCalories, false, {})
            : FALLBACK_MEAL_PLAN;

        if (window.VORYNEX.optimizer && window.VORYNEX.optimizer.optimize) {
            window.VORYNEX.optimizer.optimize(mealPlan, {
                protein: macros.protein,
                carbs: macros.carbs,
                fat: macros.fat
            });
        }

        return {
            targets: {
                calories: targetCalories,
                protein: macros.protein,
                carbs: macros.carbs,
                fat: macros.fat,
                water: water,
                trainingDay: {
                    calories: trainingDayTargetResult.value,
                    protein: trainingDayMacros.protein,
                    carbs: trainingDayMacros.carbs,
                    fat: trainingDayMacros.fat
                },
                restDay: {
                    calories: restDayTargetResult.value,
                    protein: restDayMacros.protein,
                    carbs: restDayMacros.carbs,
                    fat: restDayMacros.fat
                }
            },
            calorieBreakdown: calorieBreakdown,
            macros: macrosForUI,
            scores: scores,
            bodyFatPct: resolvedBodyFatPct,
            bodyFatSource: bodyFat.source,
            goal: goal,
            weeklyProgress: [],
            mealPlan: mealPlan,
            mealPlanMode: "training",
            recipeSelections: {},
            condimentSelections: {},
            supplementRecommendations: supplementRecommendations,
            meta: {
                bmr: Math.round(bmr),
                bmrEquationUsed: bmrResult.equationUsed,
                bmrComparison: bmrResult.comparison,
                tdee: Math.round(tdee),
                calorieFloorApplied: targetResult.flooredToBmr,
                confidence: confidence
            }
        };
    };

    /*
    ==========================================
    Export
    ==========================================
    */

    window.VORYNEX.engine = Engine;
    console.log("Engine exported.");

})();